import type { BookmarkRow } from '@bsync/shared';
import type { KeyValueStore } from './storage';

interface SavedMirror {
  cursor: number;
  rows: BookmarkRow[];
}

/** Local replica of the server's rows for this user, plus the pull cursor. */
export class Mirror {
  rows = new Map<string, BookmarkRow>();
  cursor = 0;
  private dirty = false;

  constructor(
    private readonly store: KeyValueStore,
    private readonly key = 'mirror',
  ) {}

  async load(): Promise<void> {
    const saved = await this.store.get<SavedMirror>(this.key);
    this.rows = new Map((saved?.rows ?? []).map((r) => [r.id, r]));
    this.cursor = saved?.cursor ?? 0;
  }

  /** Accept authoritative rows. Older revisions never overwrite newer ones. */
  ingest(rows: BookmarkRow[], advanceCursor: boolean): string[] {
    const changed: string[] = [];
    for (const r of rows) {
      const cur = this.rows.get(r.id);
      if (!cur || r.rev > cur.rev) {
        this.rows.set(r.id, r);
        changed.push(r.id);
      }
      if (advanceCursor && r.rev > this.cursor) this.cursor = r.rev;
    }
    if (changed.length || advanceCursor) this.dirty = true;
    return changed;
  }

  replaceAll(rows: BookmarkRow[], cursor: number): void {
    this.rows = new Map(rows.map((r) => [r.id, r]));
    this.cursor = cursor;
    this.dirty = true;
  }

  async flush(): Promise<void> {
    if (!this.dirty) return;
    this.dirty = false;
    await this.store.set<SavedMirror>(this.key, { cursor: this.cursor, rows: [...this.rows.values()] });
  }

  async clear(): Promise<void> {
    this.rows.clear();
    this.cursor = 0;
    this.dirty = false;
    await this.store.remove(this.key);
  }
}
